(ns app.agenda.preview-modal
  "The modal that the 'Preview' button of an agenda card opens: the
   post's media like in the gallery's entry modal (a video plays, other
   media show their preview image), the details of the post and its
   actions. The video URL comes from `:query/gallery-entry`, like in
   the gallery. Modal `:params`: the post's `:db/id` and its
   `:story-media/eid` (nil for a Reel whose video was uploaded directly,
   which only shows its preview image)."
  (:require [app.agenda.home :as home]
            [app.agenda.post-settings :as post-settings]
            [clojure.string :as str]
            [parts.replicant.query :as query]
            [storrito.date.format :as f]
            [storrito.ui.button :as button]
            [ui.gallery.entry-modal :as entry-modal]
            [ui.gallery.query :as gq]))

(defn find-post
  "The post from the loaded agenda, nil once the agenda no longer
   contains it."
  [state post-id]
  (some #(when (= post-id (:db/id %)) %)
        (:agenda/posts (query/get-result state (home/home-query state)))))

(defn preview-entry
  "The entry for `ui.gallery.entry-modal/preview`: the agenda's preview
   image right away, the video once `:query/gallery-entry` answered."
  [state post media-eid]
  (let [entry (when media-eid
                (query/get-result state (gq/entry-query media-eid)))]
    {:story-media/preview-url (or (home/post-preview-url post)
                                  (:story-media/preview-url entry))
     :story-media/video-url (:story-media/video-url entry)
     :story-media/name (home/post-file-name post)}))

(defn no-preview?
  "Whether no preview image can arrive any more: the post has none and
   either has no story media or its gallery entry answered (or failed)
   without one. `ui.gallery.entry-modal/preview` would spin forever then."
  [state entry media-eid]
  (and (nil? (:story-media/preview-url entry))
       (or (nil? media-eid)
           (query/available? state (gq/entry-query media-eid))
           (query/error? state (gq/entry-query media-eid)))))

(defn no-preview-box
  "In the place and size of `ui.gallery.entry-modal/preview`, which
   would spin forever without a preview image."
  []
  [:div.flex.items-center.justify-center.shrink-0.bg-gray-200.rounded.text-sm.text-gray-500
   {:class ["w-[180px]" "h-[320px]" "sm:w-[270px]" "sm:h-[480px]"]}
   "No preview"])

(defn media-preview
  [state post media-eid]
  (let [entry (preview-entry state post media-eid)]
    (if (no-preview? state entry media-eid)
      (no-preview-box)
      (entry-modal/preview entry))))

(def ^:private error-statuses
  "A failed post, and one waiting for its next retry."
  #{:error :back-off})

(defn highlights-query
  "Loads the titles of the story account's highlights, as the post only
   stores the highlight's id."
  [post]
  (when-let [user-id (and (not-empty (:instagram.story.post/highlight-id post))
                          (:instagram.story.post/user-id post))]
    {:query/kind :query/social-scheduling-highlights
     :query/data {:instagram.user/id user-id}}))

(defn long-value?
  "Whether the value goes below its label instead of next to it."
  [value]
  (let [s (str value)]
    (or (< 24 (count s))
        (str/includes? s "\n"))))

(defn row-kind
  [[label value]]
  (cond
    (boolean? value) :switch
    (contains? post-settings/text-labels label) :text
    :else :value))

(defn relative-time
  "The time from `now` to `date` in words, like 'in 3 days'."
  [now date]
  (let [minutes (js/Math.round (/ (- (.getTime date) (.getTime now)) 60000))
        distance (js/Math.abs minutes)
        hours (js/Math.round (/ distance 60))
        [n unit] (cond
                   (< distance 60) [distance "minute"]
                   (< hours 24) [hours "hour"]
                   :else [(js/Math.round (/ distance 1440)) "day"])
        words (str n " " unit (when (not= 1 n) "s"))]
    (cond
      (zero? distance) "now"
      (pos? minutes) (str "in " words)
      :else (str words " ago"))))

(defn story-format?
  "Whether the post is a story, which its viewer shows with a progress
   bar."
  [post]
  (or (= "STORY" (:schedule/post-type post))
      (and (= :instagram (home/post-platform post))
           (not (home/social-platform post)))))

(defn avatar
  "The account's picture, else its initial, else the platform's icon."
  [post account]
  (let [url (:account/profile-picture-url account)
        account-name (not-empty (:account/name account))]
    (if url
      [:img.h-7.w-7.shrink-0.rounded-full.object-cover.ring-2.ring-white
       {:src url
        :alt ""}]
      [:span.flex.h-7.w-7.shrink-0.items-center.justify-center.rounded-full.bg-white.text-xs.font-semibold.text-secondary-500
       (if account-name
         ;; the first character, not the first UTF-16 unit, which
         ;; is half of an emoji:
         (str/upper-case (first (js/Array.from account-name)))
         (home/platform-icon (home/post-platform post) ["h-3.5" "w-3.5"]))])))

(defn viewer-header
  "The header of the platform's viewer above the media: the post as its
   audience will see it."
  [post account]
  [:div.pointer-events-none.absolute.inset-x-0.top-0.z-10.bg-gradient-to-b.to-transparent.px-3.pb-10.pt-2
   {:class ["from-black/60"]}
   (when (story-format? post)
     [:div.mb-2.overflow-hidden.rounded-full
      {:class ["h-0.5" "bg-white/40"]}
      [:div.h-full.rounded-full.bg-white
       {:class ["w-1/3"]}]])
   [:div.flex.items-center.gap-2
    (avatar post account)
    [:span.truncate.text-sm.font-semibold.text-white
     (or (:account/name account) (home/platform-label post))]]])

(defn switch-mark
  [on?]
  (if on?
    [:span.flex.h-4.w-4.shrink-0.items-center.justify-center.rounded-full.bg-secondary-500.text-white
     [:svg {:class ["h-2.5" "w-2.5"]
            :viewBox "0 0 12 12" :fill "none" :stroke "currentColor"
            :stroke-width "2" :aria-hidden "true"}
      [:path {:d "M2.5 6.5l2.5 2.5 4.5-6" :stroke-linecap "round" :stroke-linejoin "round"}]]]
    [:span.h-4.w-4.shrink-0.rounded-full.border.border-gray-300]))

(defn text-rows
  [rows]
  (for [[label value] rows]
    [:div {:replicant/key label}
     [:p.text-xs.font-medium.text-gray-500 label]
     [:p.mt-1.whitespace-pre-wrap.break-words.border-l-2.border-gray-300.pl-3.text-gray-800
      value]]))

(defn value-rows
  [rows]
  [:dl.flex.flex-col
   {:class ["gap-1.5"]}
   (for [[label value] rows]
     [:div.flex.flex-wrap.justify-between.gap-x-3
      {:replicant/key label}
      [:dt.text-gray-500 label]
      [:dd.break-words.font-medium.text-secondary-500
       {:class (if (long-value? value) ["basis-full"] ["text-right"])}
       value]])])

(defn switch-rows
  [rows]
  [:ul.flex.flex-col
   {:class ["gap-1.5"]}
   (for [[label on?] rows]
     [:li.flex.items-center.gap-2
      {:replicant/key label
       :class (if on? ["text-secondary-500"] ["text-gray-400"])}
      (switch-mark on?)
      [:span label]
      [:span.sr-only (if on? "on" "off")]])])

(defn settings
  "The settings that the user chose when scheduling the post, grouped
   by their form: texts, values and switches."
  [post highlights]
  (let [groups (group-by row-kind (post-settings/rows post highlights))]
    (for [[kind f] [[:text text-rows]
                    [:value value-rows]
                    [:switch switch-rows]]
          :when (seq (get groups kind))]
      [:div.flex.flex-col.gap-3.border-t.border-gray-200.pt-3
       {:replicant/key kind}
       (f (get groups kind))])))

(defn permalink
  "The link to the posted post, only a web link: it comes from the
   platform."
  [post]
  (let [url (:social.post/permalink post)]
    (when (and url (re-find #"^https?://" url))
      url)))

(defn when-block
  "The date and time of the post: the time is what a schedule is about."
  [post now]
  (when-let [date (home/post-date post)]
    [:div
     [:p.text-xs.font-semibold.uppercase.tracking-wider.text-gray-500
      (f/format f/date-formatter-short date)]
     [:p.flex.items-baseline.gap-3
      {:class ["mt-0.5"]}
      [:span.text-4xl.font-semibold.tracking-tight.tabular-nums.text-secondary-500
       (home/format-time date)]
      [:span.text-sm.font-medium
       {:class (if (< (.getTime now) (.getTime date))
                 ["text-storrito-500"]
                 ["text-gray-500"])}
       (relative-time now date)]]]))

(defn details
  [post highlights now]
  [:div.flex.w-full.flex-col.gap-4.text-sm
   ;; as high as the preview, the settings scroll:
   {:class ["sm:w-72" "sm:max-h-[480px]" "sm:overflow-y-auto"]}
   ;; clear of the modal's close button:
   [:div.flex.items-center.justify-between.gap-3.pr-8
    [:span.flex.items-center.gap-2.font-medium.text-secondary-500
     (home/platform-icon (home/post-platform post) ["h-4" "w-4"])
     (home/platform-label post)]
    (home/status-badge post)]
   (when-block post now)
   (when-let [message (and (contains? error-statuses (:status post))
                           (home/post-error-message post))]
     [:p.rounded-lg.bg-red-50.p-3.text-red-800.ring-1.ring-red-200
      message])
   (settings post highlights)
   (when-let [url (permalink post)]
     [:a.self-start.font-medium.text-secondary-500.underline.underline-offset-2
      {:href url
       :target "_blank"
       :rel "noopener noreferrer"}
      "View post ↗"])])

(defn close-button
  []
  [:button
   {:class button/button-cancel-css
    :type "button"
    :on {:click [[:ui/modal-close]]}}
   "Close"])

(defn action-bar
  [state post]
  [:div.flex.w-full.flex-wrap.gap-2.border-t.border-gray-200.pt-4
   (when-not (= :executed (:status post))
     (button/a-button {:href (home/edit-link post)} "Edit"))
   (button/a-button {:href (home/post-reschedule-link post)} "Re-schedule")
   (when (home/cancel-action-visible? post)
     (home/cancel-button state
                         post
                         (vec (concat button/css-default
                                      button/css-danger-outline
                                      button/css-md
                                      ["gap-1.5"]))
                         [[:ui/modal-close]
                          [:home/refresh]]))
   [:div.flex-grow]
   (close-button)])

(defn post-view
  "The post as its audience will see it: the `media` under the header of
   the platform's viewer, next to the time and the settings of the post,
   and the `actions` below. Also the preview of a row of the bulk
   scheduling page (`app.bulk.preview-modal`)."
  [{:keys [post account highlights media now actions]}]
  [:div.flex.flex-col.gap-5
   [:div.flex.flex-col.items-center.gap-6.sm:flex-row.sm:items-start
    [:div.relative.shrink-0.overflow-hidden.rounded-2xl.shadow-xl.ring-1
     {:class ["ring-black/10"]}
     media
     (viewer-header post account)]
    (details post highlights now)]
   actions])

(defn render
  [state]
  (let [{post-id :db/id
         media-eid :story-media/eid} (get-in state [:ui/modal :params])
        post (find-post state post-id)
        now (js/Date.)]
    (if-not post
      [:div.flex.flex-col.gap-4
       {:class entry-modal/content-width-class}
       [:p "This post no longer exists."]
       [:div.flex.justify-end
        (close-button)]]
      (post-view
        {:post post
         :account (post-settings/account
                    (query/get-result state (home/home-query state))
                    post)
         :highlights (some->> (highlights-query post)
                              (query/get-result state)
                              :instagram.user/highlights)
         :media (media-preview state post media-eid)
         :now now
         :actions (action-bar state post)}))))

(def modal
  {:modal-id :modals/agenda-post-preview
   :on-load (fn [state]
              (let [{post-id :db/id
                     media-eid :story-media/eid} (get-in state [:ui/modal :params])
                    highlights (some-> (find-post state post-id) highlights-query)]
                (cond-> []
                  media-eid (conj [:gallery/entry-query media-eid])
                  highlights (conj [:data/query highlights]))))
   :render #'render})
